import React, { useEffect, useState } from "react";
import { injectIntl } from "react-intl";
import Header from "../../components/Header/Header";
import Loader from "../../components/GeneralComponents";
import { useDispatch, useSelector } from "react-redux";
import { WalletTransactionsDetails } from "../../api/walletApi/WalletApi";
import "./style.css";
import { Divider } from "antd";
import IN from "../../assets/images/in.svg";
import OUT from "../../assets/images/out.svg";
import { isStripe } from "../../utils/paymentProvider";
import { useStripeStatement } from "../../hooks/useStripeStatement";
import EarnedTrail from "./EarnedTrail";

/**
 * One totals card, shown only when its amount is > 0 or the category has rows.
 * Earned reads green (money in); spent/withdrawn read red (money out).
 */
const TotalCard = ({ labelId, amount, tone, intl }) => (
  <div className="w-100 p-3 shadow border text-center">
    <div className="fw-bold">{intl.formatMessage({ id: labelId })}</div>
    <div className={`fs-4 ${tone}`}>${amount.toFixed(2)}</div>
  </div>
);

const Wallet = (props) => {
  const [loading, setLoading] = useState(false);
  const userTypeData = useSelector((state) => state?.common?.fullUser);
  const walletDetails = useSelector((state) => state?.wallet?.wallet);
  const totalAmount = userTypeData?.wallet;
  const stripe = isStripe();
  const {
    earned,
    spent,
    withdrawn,
    pending,
    entries,
    loading: statementLoading,
  } = useStripeStatement(stripe);
  const dispatch = useDispatch();

  // Only the legacy provider has wallet-transaction rows to fetch.
  useEffect(() => {
    if (!stripe) {
      getTransactions();
    }
  }, [stripe]);

  const getTransactions = async () => {
    setLoading(true);
    await WalletTransactionsDetails(dispatch);
    setLoading(false);
  };

  // Show a total when it has money OR the ledger carries rows for it, so an
  // in-flight/zeroed-out category with history still surfaces.
  const hasCategory = (category) =>
    entries.some((entry) => entry.category === category);
  const showEarned = earned > 0 || hasCategory("earned");
  const showSpent = spent > 0 || hasCategory("spent");
  const showWithdrawn = withdrawn > 0 || hasCategory("withdrawn");

  return (
    <>
      {(loading || statementLoading) && (
        <div className="search-loader">
          <Loader />
        </div>
      )}

      <div className="container paymentscreen mb-5">
        <Header />
        <div className="paymentscreen-inner">
          {stripe ? (
            <div className="d-flex flex-column gap-3">
              <div className="d-flex justify-content-between gap-3 flex-wrap">
                {showEarned && (
                  <TotalCard
                    labelId="EARNED"
                    amount={earned}
                    tone="text-success"
                    intl={props.intl}
                  />
                )}
                {showSpent && (
                  <TotalCard
                    labelId="SPENT"
                    amount={spent}
                    tone="text-danger"
                    intl={props.intl}
                  />
                )}
                {showWithdrawn && (
                  // Neutral, not danger: withdrawn money was cashed out to the
                  // user's own bank, so it should not read as a loss like Spent.
                  <TotalCard
                    labelId="WITHDRAWN"
                    amount={withdrawn}
                    tone=""
                    intl={props.intl}
                  />
                )}
              </div>
              {pending > 0 && (
                <div className="text-muted" data-testid="wallet-pending-line">
                  {props.intl.formatMessage(
                    { id: "WALLET_PENDING_HELD" },
                    { amount: pending.toFixed(2) }
                  )}
                </div>
              )}
              <EarnedTrail entries={entries} />
            </div>
          ) : (
            <div className="amount-card">
              <span className="fw-bold text-white text-center fs-4">
                {props.intl.formatMessage({ id: "available_balance" })}
              </span>
              <span className="fw-normal text-white mt-4 text-center fs-2">
                ${totalAmount}
              </span>
            </div>
          )}
          {/* Legacy-only: `wallet-transaction` sums a table the Stripe paths never
              write, so under Stripe this card rendered $undefined. */}
          {!stripe && (
            <div className="w-100 p-4 shadow border in-out mt-5">
              <div className="d-flex justify-content-around">
                <span>
                  IN
                  <img src={IN} alt="" className="in-icon" />
                </span>
                <span>
                  OUT
                  <img src={OUT} alt="" className="in-icon mb-1" />
                </span>
              </div>
              <Divider />
              <div className="d-flex justify-content-around">
                <span>${walletDetails?.credit}</span>
                <span>${walletDetails?.debit}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
export default injectIntl(Wallet);
